import { MenuSlug } from "menus/interface";
import { ReactNode, useState } from "react";
import { messaging } from "config/firebase/firebase";
import { FC, createContext, useContext, useEffect } from "react";
import {
  TNotification,
  INotificationContext,
  INotificationQuantity,
  TPropertyNotification,
  ICountChat,
  IResetNotificationBySlug,
} from "./interface";
import {
  useGetNotificationsSidebar,
  usePatchNotificationsSidebarBySlug,
} from "service/notification";
import { getMenuSlug, getProperty } from "service/auth";
import { useAppAuth } from "providers/app-auth";
import { usePostTokenRegisterFCM } from "service/administrator";
import { useQueryClient } from "@tanstack/react-query";
import { MENUS_NOTI } from "service/notification/inteface";
import useFcmToken from "./use-fcm-token";
import { useLocation } from "react-router-dom";
import { usePatchReadMessage } from "service/property/message";

const AppNotificationContext = createContext<INotificationContext>(
  {} as INotificationContext
);

const AppNotification: FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<string>();
  const [chatCount, setChatCount] = useState<ICountChat[]>();
  const [notification, setNotification] = useState<TNotification>({});

  const { pathname } = useLocation();
  const qClient = useQueryClient();
  const property = getProperty();
  const { userMe } = useAppAuth();

  const patchRead = usePatchReadMessage();
  const patchNotification = usePatchNotificationsSidebarBySlug();
  const registerFCM = usePostTokenRegisterFCM();
  const { data } = useGetNotificationsSidebar({
    uuid: property?.uuid,
    pagination: false,
    enabled: !!userMe,
    // enabled: true,
  });

  const { token, permission } = useFcmToken();

  useEffect(() => {
    initialValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (token && userMe?.uuid && permission) registerFCMToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const initialValue = () => {
    const getNotification = data?.reduce((p, c) => {
      const slug = c.notiCategory;
      const value = p?.[slug as MenuSlug] || 0;
      const propertyUuid = c.propertyUuid;
      const quantity = notificationQuantity({ value, propertyUuid });
      return { ...p, [slug]: quantity };
    }, {} as { [K in MenuSlug]: number | TPropertyNotification });
    if (currentUser && pathname.includes("chat")) {
      return setNotification({});
    }
    return setNotification({ ...getNotification });
  };

  const registerFCMToken = () => {
    registerFCM.mutate(
      { token, userUuid: userMe?.uuid },
      {
        onError: (err) => console.error(err),
      }
    );
  };

  // Listening notification
  messaging.onMessage((event) => {
    console.log(`event:`, event);
    console.log("currentUser", currentUser);

    notificationBells(event);
    onNotificationChange(event);
    onChatChange(event);
  });

  const onChatChange = (event: any) => {
    const uuidUser = event.data.userPayloadUUID;

    const count = chatCount?.map((e) => {
      if (e.uuidUser === uuidUser) return { ...e, data: e.data + 1 };
      return e;
    });
    setChatCount(count);
  };

  const onNotificationChange = (event: any) => {
    const slug = event.data.menuSlug;

    const isChat = slug.toLowerCase().includes("chat");
    const isCurrentUser = event.data.userPayloadUUID?.includes(currentUser);
    if (isCurrentUser && pathname.includes("chat")) {
      onPatchNotificationBySlug({
        slug,
        propertyUuid: event.data.propertyUuid,
      });
      return readMessage(event.data?.userPayloadUUID);
    }

    if (!isChat) {
      if (getMenuSlug() === slug) {
        return onPatchNotificationBySlug({
          slug,
          propertyUuid: event.data.propertyUuid,
        });
      }
      return;
    }
    if (isChat && isCurrentUser) return;
    if (pathname.includes("chat")) {
      return onPatchNotificationBySlug({
        slug,
        propertyUuid: event.data.propertyUuid,
      });
    }

    const value = notification?.[slug as MenuSlug] || 0;
    const propertyUuid = event.data.propertyUuid;
    const quantity = notificationQuantity({ value, propertyUuid });
    const data = { ...notification, [slug]: quantity };
    setNotification(data);
  };

  // Count notification with menu
  const notificationQuantity = (props: INotificationQuantity) => {
    const { value, propertyUuid } = props;

    // If there is notification about the property
    if (typeof value === "object" || propertyUuid) {
      const property = value as TPropertyNotification;
      const quantity = property?.[propertyUuid] || 0;
      return { ...property, [propertyUuid]: quantity + 1 };
    }

    if (typeof value === "number") return value + 1;

    return undefined;
  };

  const resetNotificationBySlug = ({
    slug,
    propertyUuid,
    userPayloadUuid,
  }: IResetNotificationBySlug) => {
    const value = notification[slug];
    if (typeof value === "object" && propertyUuid) {
      const reset = {
        ...notification,
        [slug]: { ...value, [propertyUuid]: 0 },
      };
      setNotification(reset);
    } else {
      const reset = { ...notification, [slug]: 0 };
      setNotification(reset);
    }

    onPatchNotificationBySlug({
      slug,
      propertyUuid,
      userPayloadUuid,
    });
  };

  const readMessage = (uuid?: string) => {
    patchRead.mutate(
      { uuidUser: uuid },
      {
        onSuccess() {
          qClient.invalidateQueries(["chat-messages"]);
        },
        onError({ message }) {
          console.log("message", message);
        },
      }
    );
  };

  const readChat = (userUuid: string) => {
    // if (property?.uuid && typeof notification?.CHAT === "object") {
    //   const chatNotification = chatCount?.find((e) => e.uuidUser === userUuid);
    //   const resetNotification = {
    //     ...notification,
    //     CHAT: {
    //       ...notification.CHAT,
    //       [property?.uuid]:
    //         Number(notification.CHAT?.[property?.uuid] || 0) -
    //         Number(chatNotification?.data || 0),
    //     },
    //   };
    //   setNotification(resetNotification);
    // }

    onPatchNotificationBySlug({
      slug: "CHAT",
      propertyUuid: property?.uuid,
      userPayloadUuid: userUuid,
    });
    setCurrentUser(userUuid);
  };

  const onPatchNotificationBySlug = ({
    slug,
    propertyUuid,
    userPayloadUuid,
  }: IResetNotificationBySlug) => {
    patchNotification.mutate(
      { notiCategory: slug, propertyUuid, userPayloadUuid },
      {
        onSuccess: () =>
          qClient.invalidateQueries(["get-notification-sidebar"]),
        onError: (err) => console.error(err),
      }
    );
  };

  const notificationBells = (event: any) => {
    const findNoti = MENUS_NOTI.find(
      (e) => e.notiCategory === event.data.menuSlug
    );
    if (findNoti) {
      console.log("event noti bell >>>>>", event);
      qClient.invalidateQueries(["get-notification"]);
    }
  };

  return (
    <AppNotificationContext.Provider
      value={{
        notification,
        resetNotificationBySlug,
        chatCount,
        setChatCount,
        readChat,
        setCurrentSelectUser: setCurrentUser,
      }}
    >
      {children}
    </AppNotificationContext.Provider>
  );
};

export const useNotification = () => useContext(AppNotificationContext);
export default AppNotification;
